import { productEvent } from "@shared/analytics/productEvents";
import { DeltaBadge } from "@shared/components/Metrics/DeltaBadge";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import { MetricValue } from "@solomon-labs/design/components/metrics";
import { TabsMenu, TabTrigger, Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { ApyHistoryChart } from "./ApyHistoryChart";
import { DistributionHistory } from "./DistributionHistory";

type YieldRange = "7d" | "30d" | "90d" | "1y" | "all";
/** The value is the window the hook filters by; the label is what is shown. */
const YIELD_RANGES: readonly { value: YieldRange; label: string }[] = [
    { value: "7d", label: "1w" },
    { value: "30d", label: "1m" },
    { value: "90d", label: "1q" },
    { value: "1y", label: "1y" },
    { value: "all", label: "All" }
] as const;

const formatApyTooltip = (value: number): string => `${value.toFixed(2)}%`;

interface YieldContentProps {
    apyValue: string;
    liveApy?: string;
    /** Signed APY change over the active range (current − first). */
    apyChange: number | null;
    /** Period label paired with `apyChange` (e.g. "30d", "all time"). */
    apyPeriodLabel: string;
    chartPoints: number[];
    /** Optional timestamps for time-proportional chart spacing. */
    chartTimestamps?: number[];
    chartLabels: string[];
    /**
     * Points available across all time, not just the selected range. The
     * section is gated on this so a sparse window never hides the control that
     * would widen it.
     */
    allTimePoints?: number;
    /** Points each range would plot, keyed by range. Ranges below the floor
     *  are not offered. Omitted means offer them all. */
    pointsByRange?: Partial<Record<YieldRange, number>>;
    chartStartLabel: string;
    chartEndLabel: string;
    loading?: boolean;
    range: YieldRange;
    onRangeChange: (range: YieldRange) => void;
    headlineValueVariant?: "number-lg";
}

/**
 * Fewer points than this is a handful of readings, not a history, and a line
 * drawn through them reads as a trend the data cannot support. A range below
 * it is not offered as a tab, and when no range clears it the whole section
 * stays off the page rather than framing an apology.
 */
const MINIMUM_POINTS = 5;

export function YieldContent({
    apyValue,
    liveApy,
    apyChange,
    apyPeriodLabel,
    chartPoints,
    chartTimestamps,
    chartLabels,
    allTimePoints,
    pointsByRange,
    chartStartLabel,
    chartEndLabel,
    loading = false,
    range,
    onRangeChange,
    headlineValueVariant = "number-lg"
}: YieldContentProps): React.ReactElement {
    // While loading, the points have not arrived yet: render the section with
    // its skeletons rather than flashing the page without it.
    // Without counts from the caller every range is offered, and the section
    // falls back to judging itself on what it was handed.
    const offeredRanges = React.useMemo(
        () =>
            YIELD_RANGES.filter(
                (candidate) =>
                    (pointsByRange?.[candidate.value] ?? MINIMUM_POINTS) >=
                    MINIMUM_POINTS
            ),
        [pointsByRange]
    );

    const hasEnoughHistory = pointsByRange
        ? offeredRanges.length > 0
        : (allTimePoints ?? chartPoints.length) >= MINIMUM_POINTS;
    const showApyHistory = liveApy !== undefined || loading || hasEnoughHistory;

    return (
        <div className="flex flex-col gap-8 md:gap-10">
            {/* APY History card — heading, divider, then chart area */}
            {liveApy !== undefined && !hasEnoughHistory
                ? null
                : showApyHistory && (
                      <div
                          data-motion-section
                          style={{
                              opacity: 0,
                              animation:
                                  "page-fade-up 360ms ease-out 0ms forwards"
                          }}
                      >
                          <SectionHeading>APY History</SectionHeading>
                          <div className="py-5">
                              <div className="mb-5 flex items-start justify-between">
                                  <MetricValue
                                      value={liveApy ?? apyValue}
                                      valueVariant={headlineValueVariant}
                                      indicator
                                      change={
                                          liveApy === undefined ? (
                                              <DeltaBadge
                                                  value={apyChange}
                                                  period={apyPeriodLabel}
                                                  variant="small"
                                              />
                                          ) : undefined
                                      }
                                      changeVariant="small"
                                      size="lg"
                                      loading={loading}
                                  />
                                  {hasEnoughHistory && (
                                      <TabsMenu
                                          value={range}
                                          onValueChange={(v) => {
                                              productEvent(
                                                  "stats:range_selected",
                                                  {
                                                      entry_point: "stats_page",
                                                      section: "apy_history",
                                                      range: v
                                                  }
                                              );
                                              onRangeChange(v as YieldRange);
                                          }}
                                      >
                                          {offeredRanges.map((r) => (
                                              <TabTrigger
                                                  key={r.value}
                                                  value={r.value}
                                                  size="sm"
                                              >
                                                  {r.label}
                                              </TabTrigger>
                                          ))}
                                      </TabsMenu>
                                  )}
                              </div>
                              {hasEnoughHistory &&
                                  (!loading && chartPoints.length === 0 ? (
                                      <div className="flex h-[120px] items-center justify-center">
                                          <Text
                                              variant="body"
                                              className="text-foreground-muted"
                                          >
                                              No APY data in this range
                                          </Text>
                                      </div>
                                  ) : (
                                      <ApyHistoryChart
                                          id="stats-apy-history"
                                          replayKey={range}
                                          points={chartPoints}
                                          xValues={chartTimestamps}
                                          labels={chartLabels}
                                          startLabel={chartStartLabel}
                                          endLabel={chartEndLabel}
                                          formatValue={formatApyTooltip}
                                          height={120}
                                          endDot
                                          loading={loading}
                                      />
                                  ))}
                          </div>
                      </div>
                  )}

            {/* Distributions section */}
            {/* Was a two-column flex holding the teaser card beside a side
                rail. The record fills the column now, and a flex row made it
                shrink to its own 720px min-width inside a 1024px parent. */}
            <div
                data-motion-section
                className="flex flex-col"
                style={{
                    opacity: 0,
                    animation: "page-fade-up 360ms ease-out 80ms forwards"
                }}
            >
                {/* Source APY and product-wide distributions use separate APIs.
                    Do not attach the source rate to a published distribution. */}
                <DistributionHistory />
            </div>
        </div>
    );
}
